import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  InputAdornment,
  Alert,
  Snackbar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Avatar,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Search,
  Edit,
  VpnKey,
  AdminPanelSettings,
  Person,
  Block,
  CheckCircle,
  DeleteForever,
} from '@mui/icons-material';
import { subscribeToUsers, createUser as createUserDoc, updateUser, setUserStatus, deleteUser } from '../firebase/userService';
import { createUser as createAuthUser } from '../firebase/authService';
import { logActivity, ACTIONS, ENTITY_TYPES } from '../firebase/activityLogService';

const Staff = ({ currentUser }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ 
    name: '', 
    role: '', 
    email: '', 
    phone: '', 
    userType: 'staff',
    password: '',
    adminPassword: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const unsubscribe = subscribeToUsers((newData) => {
      setUsers(prevUsers => {
        // Intelligent merge - protect user identity fields
        const existingMap = new Map(prevUsers.map(u => [u.id, u]));
        
        return newData.map(newUser => {
          const existing = existingMap.get(newUser.id);
          
          if (!existing) {
            return newUser; // New user, use as-is
          }
          
          // PROTECT IMMUTABLE USER IDENTITY FIELDS
          return {
            ...newUser,
            // Critical fields that should NEVER change
            email: existing.email,
            name: existing.name,
            uid: existing.uid,
            createdAt: existing.createdAt,
          };
        });
      });
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeCount = users.filter(u => !u.disabled).length;
  const adminCount = users.filter(u => u.userType === 'admin').length;
  const disabledCount = users.filter(u => u.disabled).length;

  const handleSave = async () => {
    if (submitting) return; // Prevent double-click

    if (!formData.name.trim() || !formData.email.trim()) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    setSubmitting(true);

    try {
      if (editingUser) {
        // Update existing user
        const updateData = {
          name: formData.name,
          displayName: formData.name,
          role: formData.role || 'Staff Member',
          phone: formData.phone || '',
          status: formData.status || 'Active',
        };

        const result = await updateUser(editingUser.id, updateData);
        if (result.success) {
          closeModal();
          showSnackbar('User updated successfully');
          
          // Log activity
          await logActivity({
            userId: currentUser?.uid,
            userEmail: currentUser?.email,
            userName: currentUser?.displayName || currentUser?.email,
            action: ACTIONS.UPDATED_STAFF,
            entityType: ENTITY_TYPES.STAFF,
            entityId: editingUser.id,
            entityName: formData.name,
            details: { role: formData.role, status: formData.status }
          });
        } else {
          showSnackbar(result.error || 'Failed to update user', 'error');
        }
      } else {
        // Create new user
        if (!formData.password || formData.password.length < 8) {
          showSnackbar('Password must be at least 8 characters', 'error');
          return;
        }

        if (!formData.adminPassword) {
          showSnackbar('Please enter your admin password to create the account', 'error');
          return;
        }

        // Create Firebase Auth account
        const authResult = await createAuthUser(
          formData.email, 
          formData.password, 
          {
            name: formData.name,
            role: formData.userType,
          },
          currentUser?.email,
          formData.adminPassword
        );

        if (!authResult.success) {
          showSnackbar(authResult.error || 'Failed to create authentication account', 'error');
          return;
        }

        // Create user document in Firestore
        const userData = {
          email: formData.email,
          name: formData.name,
          displayName: formData.name,
          phone: formData.phone || '',
          role: formData.role || 'Staff Member',
          status: 'Active',
          userType: formData.userType,
          createdBy: currentUser?.uid,
        };

        const result = await createUserDoc(authResult.user.uid, userData);
        if (result.success) {
          closeModal();
          showSnackbar(`${formData.userType === 'admin' ? 'Admin' : 'Staff'} account created successfully`);
          
          // Log activity
          await logActivity({
            userId: currentUser?.uid,
            userEmail: currentUser?.email,
            userName: currentUser?.displayName || currentUser?.email,
            action: ACTIONS.CREATED_STAFF,
            entityType: ENTITY_TYPES.STAFF,
            entityId: authResult.user.uid,
            entityName: formData.name,
            details: { 
              email: formData.email, 
              role: formData.role, 
              userType: formData.userType 
            }
          });
        } else {
          // Auth user was created but Firestore profile write failed.
          // The login account exists but has no profile — show a specific message
          // so the admin knows to retry (which will fail on Auth but succeed on Firestore
          // if they use the same email, or they can delete from Firebase console).
          showSnackbar(
            `Login account created but profile save failed: ${result.error || 'Unknown error'}. ` +
            `Please try creating the account again with the same email — the profile will be saved on retry.`,
            'error'
          );
        }
      }
    } catch (error) {
      console.error('Error saving user:', error);
      showSnackbar('Failed to save user', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({ 
      name: user.name || '',
      role: user.role || 'Staff Member',
      email: user.email || '',
      phone: user.phone || '',
      status: user.status || 'Active',
      userType: user.userType || 'staff',
      password: '',
      adminPassword: '',
    });
    setShowModal(true);
  };

  const handleToggleStatus = async (user) => {
    // const action = user.disabled ? 'enable' : 'disable';
    const confirmMessage = user.disabled 
      ? `Enable ${user.name}? They will be able to log in again.`
      : `Disable ${user.name}? They will not be able to log in.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    const newStatus = !user.disabled;
    const result = await setUserStatus(user.id, newStatus);
    
    if (result.success) {
      showSnackbar(`User ${newStatus ? 'disabled' : 'enabled'} successfully`);
      
      // Log activity
      await logActivity({
        userId: currentUser?.uid,
        userEmail: currentUser?.email,
        userName: currentUser?.displayName || currentUser?.email,
        action: ACTIONS.UPDATED_STAFF,
        entityType: ENTITY_TYPES.STAFF,
        entityId: user.id,
        entityName: user.name,
        details: { action: newStatus ? 'disabled' : 'enabled' }
      });
    } else {
      showSnackbar(result.error || 'Failed to update user status', 'error');
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteConfirmUser || deleting) return;
    setDeleting(true);
    try {
      const result = await deleteUser(deleteConfirmUser.id);
      if (result.success) {
        showSnackbar(`${deleteConfirmUser.name || deleteConfirmUser.email} deleted`);
        await logActivity({
          userId: currentUser?.uid,
          userEmail: currentUser?.email,
          userName: currentUser?.displayName || currentUser?.email,
          action: ACTIONS.DELETED_STAFF,
          entityType: ENTITY_TYPES.STAFF,
          entityId: deleteConfirmUser.id,
          entityName: deleteConfirmUser.name || deleteConfirmUser.email,
          details: { email: deleteConfirmUser.email }
        });
      } else {
        showSnackbar(result.error || 'Failed to delete user', 'error');
      }
    } catch (error) {
      showSnackbar('Failed to delete user', 'error');
    } finally {
      setDeleting(false);
      setDeleteConfirmUser(null);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({ 
      name: '', 
      role: '', 
      email: '', 
      phone: '', 
      userType: 'staff',
      password: '',
      adminPassword: '',
    });
  };

  const StatCard = ({ icon, value, label, subtitle }) => (
    <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'action.hover',
              color: 'text.primary',
              display: 'flex',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {icon}
          </Box>
        </Box>
        <Typography variant="h4" fontWeight={700} gutterBottom color="text.primary">
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          {label}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Staff Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage team members and user accounts
        </Typography>
      </Box>

      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard value={users.length} label="Total Users" icon={<Person />} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard value={activeCount} label="Active Users" icon={<CheckCircle />} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard value={adminCount} label="Administrators" icon={<AdminPanelSettings />} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard value={disabledCount} label="Disabled" icon={<Block />} />
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight={600}>
              All Users
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => setShowModal(true)}>
              Create New Account
            </Button>
          </Box>

          <TextField
            fullWidth
            placeholder="Search by name, role, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id} hover sx={{ opacity: user.disabled ? 0.6 : 1 }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: '#fff', color: '#000', fontWeight: 700, fontSize: '0.9rem' }}>
                          {user.name ? user.name.split(' ').map(n => n.charAt(0)).join('').toUpperCase() : 'U'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {user.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.userType === 'admin' ? 'Administrator' : 'Staff'}
                        size="small"
                        color="default"
                        icon={user.userType === 'admin' ? <AdminPanelSettings sx={{ color: '#000 !important' }} /> : <Person sx={{ color: '#000 !important' }} />}
                        variant="filled"
                        sx={{
                          fontWeight: 700,
                          backgroundColor: '#fff',
                          color: '#000',
                          borderRadius: '20px',
                        }}
                      />
                    </TableCell>
                    <TableCell>{user.phone || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.disabled ? 'Inactive' : 'Active'}
                        size="small"
                        color="success"
                        icon={user.disabled ? <Block /> : <CheckCircle />}
                        sx={{
                          fontWeight: 700,
                          '& .MuiChip-label': {
                            px: 1,
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => handleEdit(user)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={user.disabled ? 'Enable User' : 'Disable User'}>
                        <IconButton
                          size="small"
                          color={user.disabled ? 'success' : 'error'}
                          onClick={() => handleToggleStatus(user)}
                        >
                          {user.disabled ? <CheckCircle fontSize="small" /> : <Block fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={user.id === currentUser?.uid ? "You can't delete your own account" : 'Delete User'}>
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={user.id === currentUser?.uid}
                            onClick={() => setDeleteConfirmUser(user)}
                          >
                            <DeleteForever fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No users found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showModal} onClose={closeModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VpnKey color="primary" />
            {editingUser ? 'Edit User' : 'Create New Account'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {!editingUser && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Quick Account Creation
                </Typography>
                <Typography variant="caption">
                  Creates a login account instantly. You can add job role and phone number later by editing the user.
                </Typography>
              </Alert>
            )}

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>User Type</InputLabel>
              <Select
                value={formData.userType}
                label="User Type"
                onChange={(e) => setFormData({ ...formData, userType: e.target.value })}
                disabled={editingUser}
              >
                <MenuItem value="staff">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Person fontSize="small" />
                    Staff Member
                  </Box>
                </MenuItem>
                <MenuItem value="admin">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AdminPanelSettings fontSize="small" />
                    Administrator
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Full Name"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              sx={{ mb: 2 }}
              placeholder="Enter full name"
            />
            
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              sx={{ mb: 2 }}
              disabled={editingUser}
              helperText={editingUser ? "Email cannot be changed" : "Used for login"}
              placeholder="user@example.com"
            />
            
            {!editingUser && (
              <>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  sx={{ mb: 2 }}
                  helperText="Minimum 8 characters - for the new user"
                />
                <TextField
                  fullWidth
                  label="Your Admin Password"
                  type="password"
                  required
                  value={formData.adminPassword}
                  onChange={(e) => setFormData({ ...formData, adminPassword: e.target.value })}
                  sx={{ mb: 3 }}
                  helperText="Enter YOUR password to stay logged in"
                />
              </>
            )}
            
            {editingUser && (
              <>
                <TextField
                  fullWidth
                  label="Job Role (Optional)"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  sx={{ mb: 2 }}
                  placeholder="e.g. Manager, Cashier, Sales Associate"
                />
                
                <TextField
                  fullWidth
                  label="Phone Number (Optional)"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  sx={{ mb: 2 }}
                  placeholder="+1234567890"
                />
                
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="On Leave">On Leave</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeModal} disabled={submitting}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSave} 
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <VpnKey />}
          >
            {submitting ? (editingUser ? 'Updating...' : 'Creating...') : (editingUser ? 'Update' : 'Create Account')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Confirmation */}
      <Dialog open={!!deleteConfirmUser} onClose={() => setDeleteConfirmUser(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete User?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Delete <strong>{deleteConfirmUser?.name || deleteConfirmUser?.email}</strong>? This removes their profile and all module access immediately and cannot be undone.
          </Typography>
          <Alert severity="warning">
            This only deletes their account record here. Their login (Firebase Authentication) is not removed by this app —
            to fully block them from ever logging in again, also delete their account from the Firebase Console under Authentication.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirmUser(null)} disabled={deleting}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteUser}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteForever />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Staff;
